import { TreeSelect } from 'antd';
import { connect } from 'dva';
import React, { PureComponent } from 'react';
import Media from 'react-media';
import { getProviderFactory, NotificationUtil, SIcon } from 'slipstream-components';
import { AppUtil } from 'ui-common';
import iamAPI from '../../apis/IamAPI';
import styles from './TenantSwitch.less';

interface ITenantSwitchState {
  searchValue?: string;
  expandedKeys: string[];
  treeData: any;
  onlyOneProject?: boolean;
}

interface ITenantSwitchProps {
  project: string;
  isMobile?: boolean;
  dispatch: any;
}

class TenantSwitchComp extends PureComponent<ITenantSwitchProps, ITenantSwitchState> {
  private hasMatch: boolean = false;

  constructor(props: ITenantSwitchProps) {
    super(props);
    this.state = {
      expandedKeys: [],
      treeData: null,
    };
    this.handleTenantNotification = this.handleTenantNotification.bind(this);
  }
  public componentDidMount() {
    this.populateProjects();
    NotificationUtil.getInstance().subscribe(
      ['data-view-central:tenant'],
      this.handleTenantNotification
    );
  }

  public onChange = value => {
    this.props.dispatch({
      type: 'login/extraLoginAction',
      payload: {
        project: value,
      },
      action: 'switchProject',
    });
  };

  public onSearch = searchValue => {
    if (searchValue == null) {
      return;
    }
    const lowerSearchValue = searchValue.toLowerCase();
    const expandedKeys = [];
    const match = list => {
      let hasMatch = false;
      for (const item of list) {
        const index = item.originalTitle.toLowerCase().indexOf(lowerSearchValue);
        const selfMatch = index !== -1;
        hasMatch = hasMatch || selfMatch;
        if (selfMatch) {
          const firstPart = item.originalTitle.substring(0, index);
          const highlightPart = item.originalTitle.substring(
            index,
            index + lowerSearchValue.length
          );
          const lastPart = item.originalTitle.substring(index + lowerSearchValue.length);
          item.title = (
            <div className={styles.tree_node}>
              <div className={styles.tree_node_label}>
                <span>
                  <span>{firstPart}</span>
                  <span style={{ fontWeight: 700 }}>{highlightPart}</span>
                  <span>{lastPart}</span>
                </span>
              </div>
              <div className={styles.tree_node_type}>{item.tenantRole}</div>
            </div>
          );
        } else {
          item.title = (
            <div className={styles.tree_node}>
              <div className={styles.tree_node_label}>{item.originalTitle}</div>
              <div className={styles.tree_node_type}>{item.tenantRole}</div>
            </div>
          );
        }
        // }
        if (item.children) {
          const itemMatch = match(item.children);
          if (itemMatch) {
            expandedKeys.push(item.key);
            hasMatch = true;
          }
        }
      }
      return hasMatch;
    };

    const revert = list => {
      for (const item of list) {
        const text = item.originalTitle;
        const tenantRole = item.tenantRole;
        item.title = (
          <div className={styles.tree_node}>
            <div className={styles.tree_node_label}>{text}</div>
            <div className={styles.tree_node_type}>{tenantRole}</div>
          </div>
        );
        if (item.children) {
          revert(item.children);
        }
      }
    };
    if (searchValue !== '') {
      const hasMatch = match(this.state.treeData);
      this.hasMatch = hasMatch;
    } else {
      revert(this.state.treeData);
      expandedKeys.push(this.state.treeData[0].key);
    }
    this.setState({
      expandedKeys,
      searchValue,
      treeData: this.state.treeData.slice(0),
    });
  };

  public onTreeExpand = expandedKeys => {
    this.setState({ expandedKeys });
  };

  public render() {
    if (this.state.onlyOneProject) {
      return;
    }
    const resourceMap = getProviderFactory()
      .getResourceProvider()
      .getSystemIcons();
    const icon = (
      <SIcon
        svgSource={resourceMap.get('icon_arrow')}
        style={{ transform: 'rotate(90deg)' }}
        styleType={'default'}
      />
    );
    return (
      <TreeSelect
        className={styles.tenant_switch}
        value={this.state.treeData ? this.props.project : null}
        dropdownClassName={styles.dropdown_style}
        treeData={this.state.treeData}
        suffixIcon={icon}
        onChange={this.onChange}
        onDropdownVisibleChange={(open: boolean) => {
          if (open === true) {
            if (this.hasMatch) {
              this.onSearch(this.state.searchValue);
            }
          }
        }}
        showSearch={true}
        onSearch={this.onSearch}
        treeExpandedKeys={this.state.expandedKeys}
        searchValue={this.state.searchValue}
        onTreeExpand={this.onTreeExpand}
        treeNodeFilterProp="originalTitle"
      />
    );
  }

  private handleTenantNotification(evt, msg) {
    if (msg.oper === 'CREATE' || msg.oper === 'DELETE') {
      this.populateProjects();
    }
  }

  private getAdditionalName(customerId, customers) {
    let additionalName = '';
    const customer = customers.find(c => c.uuid === customerId);
    if (customer && customer.user_defined_properties) {
      const additionalNameProp = customer.user_defined_properties.find(
        ele => ele.name === 'additional_name'
      );
      if (additionalNameProp) {
        additionalName = additionalNameProp.value;
      }
    }
    return additionalName;
  }

  private createProjectList(projects, customers) {
    return projects
      .filter(p => p.name === 'default-project' || p.scope != null)
      .map(p => {
        return {
          name: p.name,
          display_name: p.display_name || p.name,
          id: p.id,
          scope: p.scope,
          parent_project_id: p.parent_project_id,
          customer_uuid: p.customer_uuid,
          additional_name: this.getAdditionalName(p.customer_uuid, customers),
        };
      })
      .sort((a, b) => {
        // make default-project first
        if (a.name === 'default-project') {
          return -1;
        } else if (b.name === 'default-project') {
          return 1;
        } else {
          if (a.scope === 'OpCo') {
            return -1;
          } else if (b.scope === 'OpCo') {
            return 1;
          }
        }
        return a.name > b.name ? 1 : -1;
      });
  }
  private async populateProjects() {
    const r = await iamAPI.getProjects();
    const customers = await iamAPI.getCustomers();
    const result = r.body;
    const projectList = this.createProjectList(result.projects, customers);
    AppUtil.context.setProjects(projectList);

    if (projectList.length === 0 && projectList[0].name !== 'default-project') {
      this.setState({
        onlyOneProject: true,
      });
    }

    const treeData = [];
    const processedList = projectList.map(p => {
      const id = p.id;
      let text = p.display_name;
      let tenantRole = '';
      const pid = p.parent_project_id;
      if (p.name === 'default-project') {
        text = 'Global';
      }
      if (p.scope === 'Enterprise') {
        tenantRole = 'Tenant';
      } else if (p.scope === 'OpCo') {
        tenantRole = 'OpCo';
      }
      const additionalName = p.additional_name ? `(${p.additional_name})` : '';
      const node = {
        key: id,
        value: p.name,
        originalTitle: text,
        title: (
          <div className={styles.tree_node}>
            <div className={styles.tree_node_label}>
              {text} {additionalName}
            </div>
            <div className={styles.tree_node_type}>{tenantRole}</div>
          </div>
        ),
        name: p.name,
        customer_uuid: p.customer_uuid,
        pid,
        scope: p.scope,
        tenantRole,
        matched: false,
      };
      return node;
    });
    const dataMap = {};
    processedList.forEach(proj => {
      dataMap[proj.key] = proj;
      if (proj.pid) {
        const pNode = dataMap[proj.pid];
        if (pNode) {
          pNode.children = pNode.children || [];
          pNode.children.push(proj);
          proj.matched = true;
        } else {
          delete proj.pid;
        }
      }
    });

    const keys = Object.keys(dataMap);
    keys.forEach(key => {
      const node = dataMap[key];
      if (node.matched) {
        delete dataMap[key];
      } else {
        treeData.push(node);
      }
      delete node.matched;
    });

    const sortTreeData = list => {
      list.sort((a, b) => {
        if (a.name === 'default-project') {
          return -1;
        } else if (b.name === 'default-project') {
          return 1;
        } else {
          if (
            (a.scope === 'OpCo' && b.scope === 'OpCo') ||
            (a.scope === 'Enterprise' && b.scope === 'Enterprise')
          ) {
            return a.name.localeCompare(b.name);
          } else if (a.scope === 'Enterprise' && b.scope === 'OpCo') {
            return -1;
          } else if (a.scope === 'OpCo' && b.scope === 'Enterprise') {
            return 1;
          }
          return a.name.localeCompare(b.name);
        }
      });

      list.forEach(l => {
        if (l.children) {
          sortTreeData(l.children);
        }
      });
    };

    sortTreeData(treeData);

    this.setState({
      treeData,
      expandedKeys: treeData.length > 0 ? [treeData[0].key] : null,
    });
  }
}

const TenantSwitch = connect(({ login }) => ({
  project: login.authCtx.project,
}))((props: any) => (
  <Media query="(max-width: 599px)">
    {isMobile => <TenantSwitchComp {...props} isMobile={isMobile} />}
  </Media>
));

export default TenantSwitch;
